import { clearSessionStorage, clearStorage, getObj, updateObj } from './localStorage';
import { getUserLanguage } from './language';

const { version } = require('../package.json');

export const getUrlParameter = (name, url = window.location.href) => {
  try {
    const urlObj = new URL(url);
    const value = urlObj.searchParams.get(name);
    return value === 'null' || value === 'undefined' || value === 'UNDEFINED' ? null : value;
  } catch {
    return null;
  }
};

export const getUrlParameters = (url = window.location.href) => {
  const urlObject = new URL(url);
  const params = Array.from(new URLSearchParams(urlObject.search));
  return params.reduce((acc, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});
};

// KL-16213: replaceState, not pushState. Removing a parameter is not a step
// the customer took; a pushed entry per call left the cart handoff with 5+
// entries of the same page, and back went through them with nothing changing.
export const removeUrlParameter = (name) => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);

  params.delete(name);
  url.search = params.toString();

  window.history.replaceState(window.history.state, window.document.title, url.toString());
};

// KL-16213: true when the customer reached this page with back or forward
// (and the browser loaded it again rather than restoring it). A step that
// moves on by itself must not do so then, or back lands here and is sent
// forward again.
export const isHistoryVisit = () => {
  const [entry] = window.performance?.getEntriesByType?.('navigation') || [];
  return entry?.type === 'back_forward';
};

export const updateUrlParameter = (label, text) => {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  const invalidValues = [null, undefined, '', 'undefined', 'UNDEFINED'];
  if (invalidValues.includes(text)) {
    params.delete(label);
  } else {
    params.set(label, text);
  }

  url.search = params.toString();
  return window.history.replaceState(null, null, url.toString());
};

// `replace`: for a step that only redirects (a dispatcher, a guard, a step the
// customer already finished). It takes the current page's place in history, so
// back skips it instead of landing on it and being sent forward again (KL-16213).
export const navigateWithParams = (newUrl, { replace = false } = {}) => {
  const params = new URLSearchParams(window.location.search);
  const currentUrl = new URL(window.location.href);

  // On localhost, preserve the language prefix from the current path (e.g. /de/)
  // Production and Firebase dev use subdomains (de.klikkie.com, *-dev-de.web.app) so no prefix needed
  const isLocal = currentUrl.hostname === 'localhost' || currentUrl.hostname === '127.0.0.1';
  let prefixedUrl = newUrl;
  if (isLocal) {
    const langMatch = currentUrl.pathname.match(/^\/(de|nl|fr|es)\//);
    if (langMatch && !newUrl.startsWith(`/${langMatch[1]}/`)) {
      // Ensure a / between prefix and route (some routes lack a leading /)
      const sep = newUrl.startsWith('/') ? '' : '/';
      prefixedUrl = `/${langMatch[1]}${sep}${newUrl}`;
    }
  }

  const newUrlObject = new URL(prefixedUrl, currentUrl.origin);

  // Add current page's query params to newUrlObject
  [...params].forEach(([key, value]) => {
    newUrlObject.searchParams.set(key, value);
  });

  // Add newUrl's query params to newUrlObject
  const newUrlParams = new URLSearchParams(new URL(prefixedUrl, currentUrl.origin).search);
  [...newUrlParams].forEach(([key, value]) => {
    newUrlObject.searchParams.set(key, value);
  });

  if (replace) {
    window.location.replace(newUrlObject.href);
    return;
  }
  window.location.href = newUrlObject.href;
};

export const updatePlanLinks = (country) => {
  const { host } = window.location;
  let planUrl = `https://${host}/plans/gb`;
  if (country) {
    planUrl = `https://${host}/plans/${country.toLowerCase()}`;
  } else {
    const lang = getUserLanguage();
    if (lang && lang !== 'en') {
      planUrl = `https://${host}/plans/${lang}`;
    }
  }

  const planLinks = document.querySelectorAll('.plans');
  planLinks.forEach((link) => {
    link.removeAttribute('href');
    link.addEventListener('click', (event) => {
      event.preventDefault();
      navigateWithParams(planUrl);
    });
  });
};

export const removeDevMode = () => {
  // No-op: dev tag is rendered at build time in build.js
};

export const displayDevModeBanner = () => {
  // Dev tag is rendered at build time (build.js post-processing).
  // Just set the env in localStorage for any code that still reads it.
  /* eslint-disable no-undef */
  const env = typeof __KLIKKIE_ENV__ !== 'undefined' ? __KLIKKIE_ENV__ : 'prod';
  /* eslint-enable no-undef */
  updateObj('env', { env });
};
